<template>
  <LoadingContainer :is-loading="isLoading">
    <div class="card">
      <PaginatedListHeaderTemplate
        :total="items.length"
        :selections="[]"
        :has-selections="false"
        title="Header Links"
      >
        <template v-slot:options>
          <router-link
            :to="{ name: 'content.home.header-links.add' }"
            class="button"
          >
            <span class="icon is-small">
              <i class="fa fa-plus" />
            </span>
            <span>Προσθήκη</span>
          </router-link>
        </template>
      </PaginatedListHeaderTemplate>

      <div class="card-content">
        <div class="columns">
          <div class="column is-full">
            <div class="table-wrapper">
              <table
                v-if="items.length"
                class="table is-bordered is-striped is-marginless is-fullwidth"
              >
                <thead>
                  <tr>
                    <th class="has-text-centered" />
                    <th class="has-text-centered">#</th>
                    <th>Τίτλος (EL)</th>
                    <th>Τίτλος (EN)</th>
                    <th>Link</th>
                    <th class="has-text-centered">Εμφανές</th>
                    <th class="has-text-centered">Ημ/νια Δημιουργίας</th>
                    <th class="has-text-centered">Ημ/νια Ενημέρωσης</th>
                    <th class="hidden-print">Ενέργειες</th>
                  </tr>
                </thead>

                <draggable
                  v-model="items"
                  :move="canMove"
                  tag="tbody"
                  handle=".drag-handle"
                  @change="onReorder"
                >
                  <tr v-for="(item, idx) in items" :key="item.id">
                    <td class="has-text-centered">
                      <button
                        v-tooltip="'Σύρετε για αναδιάταξη (μόνο ενεργά)'"
                        :disabled="!item.is_active"
                        class="button is-small is-white drag-handle"
                        type="button"
                      >
                        <span class="icon is-small">
                          <i class="fa fa-bars" />
                        </span>
                      </button>
                    </td>

                    <td class="has-text-centered">{{ idx + 1 }}</td>
                    <td>{{ item.title_el }}</td>
                    <td>{{ item.title_en }}</td>
                    <td class="link-cell">
                      <a :href="item.link" target="_blank" rel="noopener">
                        {{ item.link }}
                      </a>
                    </td>

                    <td class="has-text-centered">
                      <ToggleButton
                        :value="!!item.is_active"
                        :sync="true"
                        :color="{ checked: '#22A684', unchecked: '#ff3860' }"
                        :width="30"
                        :height="15"
                        @change="toggleActive(item, $event)"
                      />
                    </td>

                    <td class="has-text-centered">
                      {{ formatDateTime(item.created_at) }}
                    </td>
                    <td class="has-text-centered">
                      {{ formatDateTime(item.updated_at) }}
                    </td>

                    <td class="hidden-print">
                      <div class="buttons are-small">
                        <router-link
                          :to="{
                            name: 'content.home.header-links.edit',
                            params: { id: item.id },
                          }"
                          class="button is-small"
                        >
                          <span class="icon is-small">
                            <i class="fa fa-pencil" />
                          </span>
                        </router-link>

                        <button
                          class="button is-small is-outlined"
                          type="button"
                          @click="askToDelete(item)"
                        >
                          <span class="icon is-small">
                            <i class="fa fa-trash" />
                          </span>
                        </button>
                      </div>
                    </td>
                  </tr>
                </draggable>
              </table>

              <h4 v-else class="title is-4 has-text-centered">
                Δε βρέθηκαν στοιχεία
              </h4>
            </div>
          </div>
        </div>
      </div>

      <Confirmation
        :model-name="selected ? selected.title_el : ''"
        :is-open="isConfirmOpen"
        description="Είστε βέβαιος για τη διαγραφή αυτού του header link;"
        @closeModal="onConfirmClose"
      />
    </div>
  </LoadingContainer>
</template>

<script>
import draggable from 'vuedraggable';
import request from '@/utils/request';
import LoadingContainer from '@/views/components/LoadingContainer';
import PaginatedListHeaderTemplate from '@/views/components/PaginatedList/components/PaginatedListHeaderTemplate';
import Confirmation from '@/views/components/Confirmation';

export default {
  name: 'HeaderLinksList',
  components: {
    draggable,
    LoadingContainer,
    PaginatedListHeaderTemplate,
    Confirmation,
  },
  data: () => ({
    isLoading: false,
    items: [],
    originalSnapshot: [],
    isConfirmOpen: false,
    selected: null,
  }),
  metaInfo: { title: 'Header Links' },

  created() {
    this.fetch();
  },

  methods: {
    async fetch() {
      try {
        this.isLoading = true;
        const { data } = await request.get('/home/header-links');
        if (!data.error) {
          this.items = Array.isArray(data.data) ? data.data : [];
          this.snapshot();
        } else {
          this.$notify({
            type: 'error',
            title: 'Αποτυχία',
            text: 'Αποτυχία φόρτωσης',
          });
        }
      } catch (e) {
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: 'To αίτημα απέτυχε',
        });
      } finally {
        this.isLoading = false;
      }
    },

    snapshot() {
      this.originalSnapshot = JSON.parse(JSON.stringify(this.items));
    },

    canMove(evt) {
      const di = evt && evt.draggedContext ? evt.draggedContext.index : null;
      const ri = evt && evt.relatedContext ? evt.relatedContext.index : null;
      if (typeof di !== 'number' || typeof ri !== 'number') return false;

      const dragged = this.items[di];
      const related = this.items[ri];
      return dragged && related && !!dragged.is_active && !!related.is_active;
    },

    async onReorder() {
      // items already mutated to new order
      const active = this.items.filter(i => i.is_active);
      const payload = active.map((it, idx) => ({ id: it.id, order: idx }));

      try {
        const { data } = await request.patch('/home/header-links/reorder', {
          items: payload,
        });
        if (data.error) throw new Error(data.message || 'Server error');

        // reflect new order locally for active items
        active.forEach((it, idx) => {
          this.$set(it, 'order', idx);
        });
        this.snapshot();

        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Η αναδιάταξη ολοκληρώθηκε',
        });
      } catch (e) {
        // rollback UI
        this.items = JSON.parse(JSON.stringify(this.originalSnapshot));
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text:
            (e && e.response && e.response.data && e.response.data.message) ||
            e.message ||
            'To αίτημα απέτυχε',
        });
      }
    },

    async toggleActive(item, evt) {
      const next =
        evt && typeof evt.value !== 'undefined' ? !!evt.value : !item.is_active;
      const prev = !!item.is_active;

      // optimistic
      this.$set(item, 'is_active', next);

      try {
        const { data } = await request.patch(`/home/header-links/${item.id}`, {
          is_active: next,
        });
        if (data.error) throw new Error(data.message || 'Server error');

        if (
          next &&
          (item.order === null || typeof item.order === 'undefined')
        ) {
          await this.fetch(); // pull BE-assigned order
        }
        if (!next) this.$set(item, 'order', null);
      } catch (e) {
        // rollback
        this.$set(item, 'is_active', prev);

        const msg =
          (e && e.response && e.response.data && e.response.data.message) ||
          e.message ||
          'To αίτημα απέτυχε';
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: msg.includes('Maximum of 3')
            ? 'Μέγιστο 3 ενεργοί σύνδεσμοι'
            : msg,
        });
      }
    },

    askToDelete(item) {
      this.selected = item;
      this.isConfirmOpen = true;
    },

    async onConfirmClose(confirmed) {
      this.isConfirmOpen = false;
      if (!confirmed || !this.selected) return;

      try {
        const { data } = await request.delete(
          `/home/header-links/${this.selected.id}`,
        );
        if (data.error) throw new Error(data.message || 'Server error');
        this.$notify({
          type: 'success',
          title: 'Επιτυχία',
          text: 'Το header link διαγράφτηκε',
        });
        await this.fetch();
      } catch (e) {
        this.$notify({
          type: 'error',
          title: 'Αποτυχία',
          text: 'To αίτημα απέτυχε',
        });
      } finally {
        this.selected = null;
      }
    },

    formatDateTime(v) {
      if (!v) return '-';
      const s = String(v).trim();

      // Try simple "YYYY-MM-DD HH:MM:SS" or ISO-like strings first
      const parts = s.replace('T', ' ').split(' ');
      if (parts.length) {
        const [y, m, d] = (parts[0] || '').split('-');
        const timePart = (parts[1] || '').slice(0, 5); // HH:MM
        if (y && m && d) {
          const pad = n => String(n).padStart(2, '0');
          return `${pad(d)}-${pad(m)}-${y} ${timePart || '00:00'}`;
        }
      }

      const ts = Date.parse(s);
      if (!Number.isNaN(ts)) {
        const dt = new Date(ts);
        const pad = n => String(n).padStart(2, '0');
        return `${pad(dt.getDate())}-${pad(
          dt.getMonth() + 1,
        )}-${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
      }

      return s;
    },
  },
};
</script>

<style scoped>
.table-wrapper {
  position: relative;
}

.drag-handle {
  cursor: move;
}

.link-cell {
  max-width: 420px;
  white-space: normal;
}

.link-cell a {
  display: inline-block;
  overflow-wrap: anywhere;
  word-break: break-word;
}
</style>
